import { VisuallyHidden } from '../VisuallyHidden';
import clsx from 'clsx';
import { type ReactNode } from 'react';
import {
  Column as AriaColumn,
  TableHeader as AriaTableHeader,
  type TableHeaderProps as AriaTableHeaderProps,
} from 'react-aria-components';
import { DataTableColumn } from './Column';
import styles from './DataTable.module.scss';
import type { BetaColumnHeader } from './types';
import { buildStickyProps, useStickyColumnsContext } from './useStickyColumns';

type DataTableHeaderProps<T extends object = object> =
  AriaTableHeaderProps<T> & {
    /** Column definitions */
    columns?: BetaColumnHeader[];
    /** Whether the table is in a loading state (disables sorting) */
    isLoading?: boolean;
    /** Whether to show the bulk actions / checkbox column */
    showCheckboxes?: boolean;
    /** Component to render for bulk actions */
    bulkActionsComponent?: ReactNode;
    /** Additional inline styles */
    style?: React.CSSProperties;
    /** Tooltip to show on sortable columns when sorting is disabled */
    sortDisabledTooltip?: string;
  };

export const DataTableHeader = <T extends object>({
  bulkActionsComponent,
  columns,
  showCheckboxes,
  isLoading,
  style,
  sortDisabledTooltip,
}: DataTableHeaderProps<T>) => {
  const { getStickyInfo, canScrollLeft, canScrollRight } =
    useStickyColumnsContext();
  const checkboxOffset = showCheckboxes ? 1 : 0;

  const cbStickyProps = showCheckboxes
    ? buildStickyProps({
        info: getStickyInfo(0),
        canScrollLeft,
        canScrollRight,
      })
    : {};
  const { style: cbStickyStyle, ...cbStickyDataAttrs } = cbStickyProps;

  return (
    <AriaTableHeader className={styles.th} style={style}>
      {showCheckboxes && (
        <AriaColumn
          className={styles.bulkActionsCell}
          style={cbStickyStyle}
          {...cbStickyDataAttrs}
        >
          {bulkActionsComponent}
        </AriaColumn>
      )}
      {(columns ?? []).map(
        (
          {
            allowsSorting,
            label,
            sortKey,
            className,
            isVisuallyHidden,
            isRowHeader,
            style: colStyle,
            allowsResizing,
            defaultWidth,
            minWidth,
            maxWidth,
            sortDisabledTooltip: colSortDisabledTooltip,
          }: BetaColumnHeader,
          index: number
        ) => {
          const absIdx = index + checkboxOffset;
          const { style: stickyStyle, ...stickyDataAttrs } = buildStickyProps({
            info: getStickyInfo(absIdx),
            canScrollLeft,
            canScrollRight,
          });

          return (
            <DataTableColumn
              id={sortKey}
              allowsSorting={!isLoading && !!allowsSorting}
              className={clsx(styles.th, className)}
              key={sortKey ?? index}
              isRowHeader={isRowHeader}
              style={{ ...colStyle, ...stickyStyle }}
              allowsResizing={allowsResizing}
              defaultWidth={defaultWidth ?? '1fr'}
              minWidth={minWidth}
              maxWidth={maxWidth}
              sortDisabledTooltip={
                colSortDisabledTooltip ?? sortDisabledTooltip
              }
              {...stickyDataAttrs}
            >
              {isVisuallyHidden ? (
                <VisuallyHidden>{label}</VisuallyHidden>
              ) : (
                label
              )}
            </DataTableColumn>
          );
        }
      )}
    </AriaTableHeader>
  );
};
